import { Store } from "../core/state";
import { el, svg, clearElement, applyStyles } from "../core/dom";
import { createMediaQuery, type MediaQuerySubscription } from "../core/media-query";
import {
  fetchConsentContent,
  submitConsentEvent,
  initiateGuardianVerification,
  verifyGuardianStatus,
} from "../api";
import {
  buildLedgerConsentOverlay,
  fetchNoticeConsentStatus,
} from "../api/notice-consent-status";
import { resolveDisplayLanguage } from "../api/language";
import { resolveEndpointOptions, type ResolvedEndpoints } from "../shared/endpoints";
import { injectCheckboxStyles } from "../styles/inject";
import { styles } from "../styles/modal";
import { renderModalPurposes, type ModalPurposeRefs, type PurposeLook } from "./purposes";
import { renderLanguageSelector, type LanguageSelectorRefs } from "./language-selector";
import { setupKeyboardTrap, type KeyboardTrapCleanup } from "./keyboard";
import {
  saveConsentState,
  getConsentState,
  clearConsentState,
} from "../services/storage";
import { TTSManager } from "./tts";
import { renderAgeVerification } from "./age-verification";
import {
  renderGuardianForm,
  validateGuardianForm,
  type GuardianFormData,
  type GuardianFormRefs,
} from "./guardian-form";
import {
  renderVerificationScreen,
  getVerificationErrorMessage,
} from "./verification-screen";
import { colorsFromLook } from "./colors";
import { buildSelectionForMode } from "./consentSelection";
import {
  isMultiProduct,
  productPurposeKey,
  purposesInRenderOrder,
} from "../core/productMatrix";
import {
  confirmHint,
  isFullyConsented,
  purposeCheckedFromElements,
  purposeToggle,
  recordedSelectionLookup,
  sectionToggle,
  seedSelection,
  submissionPurposes,
} from "./productConsent";
import {
  groupsOf,
  noticeConfirmVerdict,
  productIsRequired,
  rowsOf,
  seedProductState,
} from "./productState";
import {
  renderConfirmHint,
  renderProductGroups,
  renderProductSection,
  updateConfirmHint,
  updateProductSection,
} from "./product-sections";
import { logoRowJustify, resolveLogoPosition } from "./layout";
import { OtpGateSession } from "./otpGate";
import { lockSelections, renderOtpGatePanel } from "./otp-gate-panel";
import { openConfirmDialog } from "./confirm-dialog";
import { getProductPolicyLinks, shouldShowNoticePolicyLink } from "./productPolicies";
import { renderProductPolicyLine } from "./product-policy-line";
import { applyPreselection, recordedAnswersFor } from "./preselection";
import { injectNoticeFontStyles, resolveNoticeFontFamily } from "./notice-font";
import { noticeChromeCopy } from "./chromeCopy";
import {
  ENGLISH_SECTION_LABELS,
  NOTICE_SECTIONS,
  sectionLabelsFor,
} from "./section-labels";
import {
  renderNoticeAccordion,
  type NoticeAccordionRefs,
} from "./notice-accordion";
import { buildModalLook, dockAttributes, type ModalLook } from "./look";
import { readCachedAppearance, writeCachedAppearance } from "../appearance/cache";
import {
  DARK_SCHEME_QUERY,
  DEFAULT_PURPOSE_SECTION_HEADING,
} from "../appearance/constants";
import { NoticeCustomCss } from "../appearance/custom-css";
import { part } from "../appearance/parts";
import {
  injectGlassStyles,
  injectLayoutStyles,
  injectSwitchStyles,
} from "../appearance/stylesheets";
import { applyCssVariables } from "../appearance/theme-vars";
import type { Style } from "../appearance/types";
import { prefersReducedMotion, type NoticeAppearance } from "../shared/appearance";
import {
  ACCEPT_ALL_BUTTON_ID,
  ACCEPT_SELECTED_BUTTON_ID,
  CONFIRM_HINT_COPY,
  DECLINE_BUTTON_ID,
  DEFAULT_ACCEPT_ALL_BUTTON_TEXT,
  DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT,
  DEFAULT_DECLINE_BUTTON_TEXT,
  NOTICE_FONT_ROOT_ATTRIBUTE,
  SUBMITTING_BUTTON_TEXT,
} from "../constants";
import type { SandboxOptions } from "../api/types";
import type {
  AcceptMode,
  ConfirmAction,
  ConfirmDialogRefs,
  ConfirmVerdict,
  ModalOptions,
  NoticeSection,
  OtpGatePanelRefs,
  ProductSectionRefs,
  SelectionState,
} from "../types";
import type {
  ConsentContent,
  PurposeItemData,
  TranslationObject,
} from "../api/types";

type ModalState = {
  isLoading: boolean;
  isSubmitting: boolean;
  isSubmittingAccept: boolean;
  isSubmittingDecline: boolean;
  content: ConsentContent | null;
  selectedPurposes: Record<string, boolean>;
  selectedDataElements: Record<string, boolean>;
  initialDataElementSelections: Record<string, boolean>;
  recordedAnswers: Record<string, boolean>;
  collapsedPurposes: Record<string, boolean>;
  collapsedProducts: Record<string, boolean>;
  recordedPurposeSelections: Record<string, boolean>;
  consentedProducts: Record<string, boolean>;
  consentedPurposes: Record<string, boolean>;
  hasAlreadyConsented: boolean;
  isFullyConsentedReviewMode: boolean;
  selectedLanguage: string;
  errorMessage: string | null;
  fetchError: Error | null;
  needsConsentPurposes: PurposeItemData[];
  alreadyConsentedPurposes: PurposeItemData[];
  // Age/Guardian flow
  isMinorFlow: boolean;
  selfDeclaredAdult: boolean;
  showAgeVerification: boolean;
  showGuardianForm: boolean;
  showVerificationScreen: boolean;
  verificationReference: string | null;
  isVerificationComplete: boolean;
  isInitiatingVerification: boolean;
  isPollingStatus: boolean;
  verificationError: string | null;
  verificationErrorCode: string | null;
  canRetryVerification: boolean;
  guardianFormData: GuardianFormData;
};

/** The `data-redacto-*` attributes this controller last set on an element. */
const OWNED_ATTRIBUTE_PREFIX = "data-redacto-";

/**
 * Repaints one of the notice's roots: the attributes the appearance stylesheets
 * and the admin's custom CSS key on, the inline style and the custom
 * properties. Starts from a clean element, so a look that drops a key (the
 * loader pill growing into the card) does not leave it behind.
 */
const paintRoot = (
  element: HTMLElement,
  style: Style,
  attributes: Record<string, string>,
  variables: Record<string, string> = {}
): void => {
  for (const { name } of Array.from(element.attributes)) {
    if (name.startsWith(OWNED_ATTRIBUTE_PREFIX) && !(name in attributes)) {
      element.removeAttribute(name);
    }
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
  element.removeAttribute("style");
  applyStyles(element, style);
  applyCssVariables(element, variables);
};

export class ModalController {
  private store: Store<ModalState>;
  private options: ModalOptions;
  private readonly endpoints: ResolvedEndpoints;
  private hostEl: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private previousBodyOverflow: string | null = null;
  private previousHtmlOverflow: string | null = null;
  private overlayEl: HTMLElement | null = null;
  private modalEl: HTMLElement | null = null;
  private mediaQuery: MediaQuerySubscription;
  private colorScheme: MediaQuerySubscription;
  private purposeRefs: ModalPurposeRefs | null = null;
  private alreadyConsentedPurposeRefs: ModalPurposeRefs | null = null;
  private productSections: ProductSectionRefs[] = [];
  private confirmHintEl: HTMLElement | null = null;
  private bottomSectionEl: HTMLElement | null = null;
  private langSelectorRefs: LanguageSelectorRefs | null = null;
  private keyboardCleanup: KeyboardTrapCleanup | null = null;
  private abortController: AbortController | null = null;
  private unsubscribe: (() => void) | null = null;
  private ttsManager: TTSManager | null = null;
  private guardianFormRefs: GuardianFormRefs | null = null;
  private statusPollTimeout: ReturnType<typeof setTimeout> | null = null;
  private popupCheckInterval: ReturnType<typeof setInterval> | null = null;
  private verificationPopup: Window | null = null;
  private verificationSessionToken: string | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private otpSession = new OtpGateSession();
  private otpPanel: OtpGatePanelRefs | null = null;
  /** The visitor's own ticks, held while a shortcut is pending behind the code. */
  private preGateSelection: SelectionState | null = null;
  private selectionsEl: HTMLElement | null = null;
  private confirmDialog: ConfirmDialogRefs | null = null;
  /** The last appearance served for this notice, drawn while it loads. */
  private cachedAppearance: NoticeAppearance | undefined;
  private look: ModalLook | null = null;
  private customCss = new NoticeCustomCss();
  /** The visitor's own choice for each collapsible section. */
  private openSections: Record<NoticeSection, boolean> = { about: false, dpo: false };
  private sectionRefs: Partial<Record<NoticeSection, NoticeAccordionRefs>> = {};
  private talkbackActive = false;

  // DOM refs for targeted updates
  private acceptBtn: HTMLButtonElement | null = null;
  private acceptAllBtn: HTMLButtonElement | null = null;
  private declineBtn: HTMLButtonElement | null = null;
  private middleSection: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private noticeTitleEl: HTMLElement | null = null;
  private purposeHeadingEl: HTMLElement | null = null;
  private privacyPolicyLinkEl: HTMLAnchorElement | null = null;
  private privacyCenterLinkEl: HTMLAnchorElement | null = null;
  private ttsButton: HTMLElement | null = null;

  constructor(options: ModalOptions) {
    this.options = options;
    this.endpoints = resolveEndpointOptions(options.baseUrl, options.ledgerBaseUrl);
    this.store = new Store<ModalState>({
      isLoading: true,
      isSubmitting: false,
      isSubmittingAccept: false,
      isSubmittingDecline: false,
      content: null,
      selectedPurposes: {},
      selectedDataElements: {},
      initialDataElementSelections: {},
      recordedAnswers: {},
      collapsedPurposes: {},
      collapsedProducts: {},
      recordedPurposeSelections: {},
      consentedProducts: {},
      consentedPurposes: {},
      hasAlreadyConsented: false,
      isFullyConsentedReviewMode: false,
      selectedLanguage: options.language || "en",
      errorMessage: null,
      fetchError: null,
      needsConsentPurposes: [],
      alreadyConsentedPurposes: [],
      isMinorFlow: false,
      selfDeclaredAdult: false,
      showAgeVerification: false,
      showGuardianForm: false,
      showVerificationScreen: false,
      verificationReference: null,
      isVerificationComplete: false,
      isInitiatingVerification: false,
      isPollingStatus: false,
      verificationError: null,
      verificationErrorCode: null,
      canRetryVerification: false,
      guardianFormData: { guardianName: "", guardianContact: "", guardianRelationship: "" },
    });
    this.mediaQuery = createMediaQuery("(max-width: 768px)");
    // Automatic dark mode follows the device's scheme while the notice is open.
    this.colorScheme = createMediaQuery(DARK_SCHEME_QUERY);
    // TTSManager is instantiated lazily in renderOverlayWithLoading() once the
    // shadow root exists, so it can scope its DOM queries inside the shadow.
  }

  async init(): Promise<void> {
    this.unsubscribe = this.store.subscribe((state, prevState) => {
      this.handleStateChange(state, prevState);
    });

    this.mediaQuery.subscribe(() => this.repaint());
    this.colorScheme.subscribe(() => this.repaint());

    // Draw the loading state in the look last served for this notice.
    this.cachedAppearance = readCachedAppearance(this.options.noticeId);

    // Render loading state
    this.renderOverlayWithLoading();

    // Fetch content
    await this.fetchNotice();
  }

  destroy(): void {
    this.abortController?.abort();
    this.keyboardCleanup?.();
    this.unsubscribe?.();
    this.mediaQuery.destroy();
    this.colorScheme.destroy();
    this.ttsManager?.destroy();
    this.stopPolling();
    this.confirmDialog?.dispose();
    this.confirmDialog = null;
    this.disposeSections();
    this.customCss.remove();
    this.store.destroy();

    // Removing the shadow host nukes the entire shadow tree (overlay, modal,
    // language dropdown, injected stylesheets) in a single operation.
    if (this.hostEl && this.hostEl.parentNode) {
      this.hostEl.parentNode.removeChild(this.hostEl);
    }

    // Restore the host page's scroll lock to whatever it was before we opened.
    if (this.previousBodyOverflow !== null) {
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = null;
    }
    if (this.previousHtmlOverflow !== null) {
      document.documentElement.style.overflow = this.previousHtmlOverflow;
      this.previousHtmlOverflow = null;
    }

    this.hostEl = null;
    this.shadowRoot = null;
    this.overlayEl = null;
    this.modalEl = null;
    this.purposeRefs = null;
    this.langSelectorRefs = null;
  }

  updateToken(accessToken: string): void {
    this.options = { ...this.options, accessToken };
    this.fireStashedAccept();
  }

  // ─── Private ───

  private fireStashedAccept(): void {
    if (!this.modalEl || this.confirmDialog) return;
    const mode = this.otpSession.takeStashedAccept(this.options.accessToken);
    if (mode) void this.handleAccept(mode);
  }

  private holdForOtp(mode: AcceptMode): boolean {
    const wasLocked = this.otpSession.isLocked;
    if (!this.otpSession.hold(this.options.otpGate, mode)) return false;
    if (wasLocked) {
      this.otpPanel?.render(this.otpSession.view);
      return true;
    }
    this.mountOtpGate();
    this.otpPanel?.focusDigit(0);
    this.otpPanel?.container.scrollIntoView?.({ block: "nearest" });
    return true;
  }

  private mountOtpGate(): void {
    this.otpPanel = null;
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    const look = this.look;
    if (!this.otpSession.isLocked || !this.selectionsEl || !content || !look) return;
    lockSelections(this.selectionsEl);
    const gate = this.options.otpGate;
    const chrome = noticeChromeCopy(state.selectedLanguage);
    const { styles } = look;
    this.otpPanel = renderOtpGatePanel({
      title: gate?.title ?? chrome.otpTitle,
      description: gate?.description ?? chrome.otpDescription,
      submitLabel: gate?.submitLabel ?? chrome.otpSubmit,
      verifyingLabel: chrome.otpVerifying,
      view: this.otpSession.view,
      styles: {
        panel: styles.otpGatePanel,
        title: styles.otpGateTitle,
        description: styles.otpGateDescription,
        digits: styles.otpGateDigits,
        digit: styles.otpGateDigit,
        error: styles.otpGateError,
        button: { ...styles.button, ...styles.acceptButton, ...styles.otpGateButton },
      },
      buttonStyle: look.buttons.acceptAll,
      onDigitsChange: (digits) => this.otpSession.setDigits(digits),
      onConfirm: () => void this.handleOtpConfirm(),
    });
    this.selectionsEl.insertAdjacentElement("afterend", this.otpPanel.container);
  }

  private async handleOtpConfirm(): Promise<void> {
    const verified = await this.otpSession.verify(
      this.options.otpGate,
      this.options.accessToken,
      () => this.otpPanel?.render(this.otpSession.view),
      noticeChromeCopy(this.store.getState().selectedLanguage).otpFailed
    );
    if (!this.modalEl) return;
    if (!verified) {
      this.otpPanel?.render(this.otpSession.view);
      this.otpPanel?.focusDigit(0);
      return;
    }
    this.preGateSelection = null;
    this.rebuildUI();
    this.fireStashedAccept();
  }

  private confirmFirst(kind: ConfirmAction, action: () => void): void {
    this.otpSession.dropStashedAccept();
    const state = this.store.getState();
    const look = this.look;
    if (
      !look?.appearance.confirmBeforeSubmit ||
      state.isFullyConsentedReviewMode ||
      !this.shadowRoot
    ) {
      action();
      return;
    }
    if (this.confirmDialog || state.isSubmitting) return;
    if (kind === "accept-selected" && !this.confirmVerdict(state).ok) return;
    this.confirmDialog = openConfirmDialog({
      root: this.shadowRoot,
      action: kind,
      language: state.selectedLanguage,
      onConfirm: () => {
        this.confirmDialog = null;
        action();
      },
      onCancel: () => {
        this.confirmDialog = null;
      },
      confirmStyle: {
        ...look.buttons.acceptAll,
        borderColor: look.buttons.acceptAll.backgroundColor,
      },
      skin: look.confirmSkin,
      rootAttributes: look.rootAttributes,
      rootVariables: look.cssVariables,
      fontFamily: this.modalEl?.style.fontFamily,
    });
  }

  /**
   * Sandbox auth options to forward to every API call. When `token` is set the
   * API layer sends the single `X-Consent-Token` header, carries every supplied
   * identifier in each request's payload, and reads org/workspace from these
   * fields instead of decoding the JWT. Empty in the normal JWT flow.
   */
  private sandboxParams(): SandboxOptions {
    // Legacy `sandboxSubject` (deprecated): classify into email/mobile so an
    // existing caller that still passes it keeps working unchanged.
    const legacySubject = this.options.sandboxSubject?.trim();
    const legacyEmail = legacySubject?.includes("@") ? legacySubject : undefined;
    const legacyMobile =
      legacySubject && !legacySubject.includes("@") ? legacySubject : undefined;
    return {
      token: this.options.token,
      // The static token carries no identity; carry every supplied identifier
      // (ucic/email/mobile) so the server can pick the principal by precedence
      // (UCIC > email > mobile) and keep the rest as the sandbox contact.
      // sandboxUcic / sandboxEmail / sandboxMobile stay as fallbacks.
      sandboxUcic: this.options.ucic || this.options.sandboxUcic,
      sandboxEmail: this.options.email || this.options.sandboxEmail || legacyEmail,
      sandboxMobile:
        this.options.mobile || this.options.sandboxMobile || legacyMobile,
      organisationUuid: this.options.organisationUuid,
      workspaceUuid: this.options.workspaceUuid,
    };
  }

  private async fetchNotice(): Promise<void> {
    this.abortController = new AbortController();
    this.store.setState({ isLoading: true, fetchError: null });

    try {
      // Standard flow reads the notice from the Go ledger. The magic-link flow
      // (applicationId/specific_uuid) and sandbox sessions stay on the consent
      // server: the ledger notice read supports neither (no specific_uuid, no
      // X-Consent-Token auth). Without this a ledger-minted token is rejected
      // by the consent server and the modal renders with no content.
      const noticeBaseUrl =
        this.endpoints.ledgerBaseUrl &&
        !this.options.applicationId &&
        !this.options.token
          ? this.endpoints.ledgerBaseUrl
          : this.endpoints.baseUrl;
      const data = await fetchConsentContent({
        noticeId: this.options.noticeId,
        accessToken: this.options.accessToken,
        refreshToken: this.options.refreshToken,
        baseUrl: noticeBaseUrl,
        language: this.options.language,
        specific_uuid: this.options.applicationId,
        validate_against: this.options.validateAgainst || "all",
        include_fully_consented_data: this.options.includeFullyConsentedData,
        signal: this.abortController.signal,
        ...this.sandboxParams(),
      });

      const activeConfig = data.detail.active_config;
      // Remembered for this notice's next load, so its loading state is drawn
      // in the admin's look rather than flashing classic.
      writeCachedAppearance(this.options.noticeId, activeConfig.appearance);

      // Overlay the prior-consent / reconsent state from the Go ledger's
      // check-consent. Consents live in the ledger post-cutover, so Python's
      // notice-validation overlay is empty; the notice text/config still comes
      // from Python (above). Falls back to the Python-provided values when the
      // ledger is unavailable, and is skipped for the magic-link flow
      // (applicationId), which pre-fills from a specific event.
      let purposeSelections = data.detail.purpose_selections;
      let reconsentRequired = data.detail.reconsent_required;
      let ledgerOverlayApplied = false;
      if (this.endpoints.ledgerBaseUrl && !this.options.applicationId) {
        const ledgerStatus = await fetchNoticeConsentStatus({
          // A sandbox session carries no bearer token; the read then
          // degrades to null and the server's own overlay applies.
          accessToken: this.options.accessToken ?? "",
          ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
          noticeUuid: this.options.noticeId,
          signal: this.abortController.signal,
        });
        if (ledgerStatus) {
          const overlay = buildLedgerConsentOverlay(ledgerStatus, activeConfig.purposes);
          reconsentRequired = overlay?.reconsent_required ?? false;
          purposeSelections = overlay?.purpose_selections;
          ledgerOverlayApplied = Boolean(overlay);
        }
      }

      // Initialize state
      const initialCollapsed: Record<string, boolean> = {};
      const initialDataElementSelections: Record<string, boolean> = {};

      // Categorize purposes for reconsent. With a ledger overlay, categorize
      // whenever prior consent exists (even without reconsent needed) so a
      // fully-consented principal gets the already-consented presentation /
      // review mode instead of a fresh consent flow — the ledger-mode
      // equivalent of Python's 409-driven short-circuit.
      let needsConsentPurposes: PurposeItemData[] = [];
      let alreadyConsentedPurposes: PurposeItemData[] = [];
      let isFullyConsentedReviewMode = false;
      const recordedFor = recordedSelectionLookup(
        purposeSelections,
        data.detail.product_purpose_selections
      );
      const seedRows = rowsOf(activeConfig);
      const reviewsFullRecord = Boolean(
        this.options.includeFullyConsentedData &&
          seedRows.length > 0 &&
          isFullyConsented(seedRows, recordedFor)
      );

      // Categorised in the order the notice renders them.
      const orderedPurposes = purposesInRenderOrder(
        activeConfig.products,
        activeConfig.purposes,
        activeConfig.product_purpose_order
      );
      if (purposeSelections && (reconsentRequired || ledgerOverlayApplied || reviewsFullRecord)) {
        for (const purpose of orderedPurposes) {
          const selection = purposeSelections[purpose.uuid];
          initialCollapsed[purpose.uuid] = true;

          if (selection && selection.selected && !selection.needs_reconsent) {
            alreadyConsentedPurposes.push(purpose);
            for (const de of purpose.data_elements) {
              const combinedId = `${purpose.uuid}-${de.uuid}`;
              const deSelection = selection.data_elements?.[de.uuid];
              initialDataElementSelections[combinedId] = deSelection?.selected || false;
            }
          } else {
            needsConsentPurposes.push(purpose);
          }
        }
      } else {
        needsConsentPurposes = orderedPurposes;
        for (const purpose of activeConfig.purposes) {
          initialCollapsed[purpose.uuid] = true;
        }
      }

      // Check if all purposes are already consented (review mode)
      if (
        this.options.includeFullyConsentedData &&
        needsConsentPurposes.length === 0 &&
        alreadyConsentedPurposes.length > 0
      ) {
        isFullyConsentedReviewMode = true;
      }

      const productSeed = seedProductState({
        config: activeConfig,
        purposeSelections,
        productPurposeSelections: data.detail.product_purpose_selections,
        defaultOpenProducts: this.options.defaultOpenProducts,
        includeFullyConsentedData: this.options.includeFullyConsentedData,
      });
      const productPreselection = applyPreselection(
        seedRows,
        activeConfig.purpose_preselection,
        recordedFor,
        productSeed.selectedPurposes && productSeed.selectedDataElements
          ? {
              selectedPurposes: productSeed.selectedPurposes,
              selectedDataElements: productSeed.selectedDataElements,
            }
          : seedSelection(seedRows, recordedFor)
      );

      const selectedLanguage = resolveDisplayLanguage(
        this.options.language,
        activeConfig.default_language,
        activeConfig.supported_languages_and_translations
      );

      // Check if minor flow is required, and restore in-progress verification
      // state from a previous session (e.g. user closed the SDK or returned
      // from a DigiLocker redirect mid-flow). Without this restoration, the
      // notice would be shown directly and guardian verification skipped.
      let isMinorFlow = false;
      let showAgeVerification = false;
      let restoreState: Partial<ModalState> = {};
      let pollingTokenToResume: string | null = null;
      if (data.detail.is_minor) {
        isMinorFlow = true;
        const persistedState = getConsentState();

        // Discard persisted state from a different notice
        if (persistedState?.noticeId && persistedState.noticeId !== this.options.noticeId) {
          clearConsentState();
        } else if (
          persistedState?.flowState === "digilocker-pending" &&
          persistedState.sessionToken
        ) {
          // User is mid-verification — resume the verification screen and polling
          restoreState = {
            showVerificationScreen: true,
            isPollingStatus: true,
          };
          pollingTokenToResume = persistedState.sessionToken;
        } else if (
          persistedState?.verificationResult === "verified" &&
          persistedState.verificationReference
        ) {
          // Verification completed during a redirect — show success state
          restoreState = {
            showVerificationScreen: true,
            isVerificationComplete: true,
            verificationReference: persistedState.verificationReference,
          };
          clearConsentState();
        } else if (
          persistedState?.verificationResult === "failed" ||
          persistedState?.verificationResult === "expired"
        ) {
          // Verification failed/expired during a redirect — show error
          restoreState = {
            showVerificationScreen: true,
            verificationError:
              persistedState.verificationResult === "expired"
                ? "Verification session has expired. Please try again."
                : "Verification failed. Please try again.",
            canRetryVerification: true,
          };
          clearConsentState();
        } else {
          // No in-progress verification — start with age verification
          showAgeVerification = true;
        }
      }

      this.store.setState({
        content: data,
        isLoading: false,
        selectedLanguage,
        collapsedPurposes: initialCollapsed,
        initialDataElementSelections,
        recordedAnswers: recordedAnswersFor(seedRows, recordedFor),
        needsConsentPurposes,
        alreadyConsentedPurposes,
        isFullyConsentedReviewMode,
        ...productSeed,
        ...productPreselection,
        isMinorFlow,
        showAgeVerification,
        ...restoreState,
      });

      // Resume polling after state has been applied so the verification screen
      // is visible while polling runs.
      if (pollingTokenToResume) {
        this.startStatusPolling(pollingTokenToResume);
      }
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") return;
      const error = err as Error & { status?: number };
      if (error.status === 409) {
        this.store.setState({ hasAlreadyConsented: true, isLoading: false });
        this.options.onAccept();
      } else {
        this.store.setState({ fetchError: error, isLoading: false });
        this.options.onError?.(error);
      }
    }
  }

  private handleStateChange(state: ModalState, prevState: ModalState): void {
    // Loading → content loaded
    if (prevState.isLoading && !state.isLoading && state.content) {
      this.rebuildUI();
      return;
    }

    // Already consented → close
    if (state.hasAlreadyConsented && !prevState.hasAlreadyConsented) {
      this.destroy();
      return;
    }

    // Fetch error → show error
    if (state.fetchError && !prevState.fetchError) {
      this.renderError(state.fetchError.message);
      return;
    }

    // Screen transitions (age → guardian → verification → consent)
    if (state.showAgeVerification !== prevState.showAgeVerification ||
        state.showGuardianForm !== prevState.showGuardianForm ||
        state.showVerificationScreen !== prevState.showVerificationScreen) {
      this.rebuildUI();
      return;
    }

    // Verification screen state changes
    if (state.showVerificationScreen && (
        state.isVerificationComplete !== prevState.isVerificationComplete ||
        state.verificationError !== prevState.verificationError ||
        state.isPollingStatus !== prevState.isPollingStatus ||
        state.isInitiatingVerification !== prevState.isInitiatingVerification)) {
      this.rebuildUI();
      return;
    }

    // Checkbox changes
    if (
      state.selectedPurposes !== prevState.selectedPurposes ||
      state.selectedDataElements !== prevState.selectedDataElements
    ) {
      this.updateCheckboxes(state);
    }

    // Collapse changes
    if (state.collapsedPurposes !== prevState.collapsedPurposes) {
      this.updateCollapseState(state);
    }

    if (state.collapsedProducts !== prevState.collapsedProducts) {
      this.updateProductSections(state);
    }

    // Error message
    if (state.errorMessage !== prevState.errorMessage) {
      this.updateErrorMessage(state);
    }

    // Submitting state
    if (state.isSubmittingAccept !== prevState.isSubmittingAccept ||
        state.isSubmittingDecline !== prevState.isSubmittingDecline) {
      this.updateButtonStates(state);
    }
  }

  private lookFor(state: ModalState): ModalLook {
    return buildModalLook({
      noticeId: this.options.noticeId,
      content: state.content?.detail.active_config ?? null,
      cachedAppearance: this.cachedAppearance,
      settings: this.options.settings,
      prefersDark: this.colorScheme.matches,
      isMobile: this.mediaQuery.matches,
    });
  }

  /** Redraws for a new breakpoint or colour scheme, whatever is showing. */
  private repaint(): void {
    const state = this.store.getState();
    if (state.content && !state.isLoading) {
      this.rebuildUI();
    } else if (state.isLoading) {
      this.renderLoading();
    }
  }

  /** Whether disclosures and entrances animate: the console's switch and the OS's. */
  private animates(): boolean {
    return (this.look?.layout.motion ?? true) && !prefersReducedMotion();
  }

  /**
   * Paints the overlay and the dialog root for a look: the appearance
   * stylesheets inside the shadow root, the admin's scoped custom CSS, and the
   * root attributes both carry.
   */
  private paintChrome(look: ModalLook, fontFamily: string, loading: boolean): void {
    const root = this.shadowRoot;
    if (!root || !this.modalEl) return;
    this.look = look;
    injectLayoutStyles(root);
    if (look.skin) injectGlassStyles(root);
    if (look.drawsSwitches) injectSwitchStyles(root);
    this.customCss.apply(root, this.options.noticeId, look.appearance.customCss);

    if (this.overlayEl) {
      paintRoot(this.overlayEl, look.overlayStyle, {
        ...look.rootAttributes,
        ...part("overlay"),
      });
    }

    const attributes = {
      ...look.rootAttributes,
      ...part("root"),
      ...dockAttributes(look),
      [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
    };
    // The dialog itself shrinks to a pill until the notice arrives, then grows
    // into the card: one element, so the focus trap and role hold throughout.
    const pill = loading && look.showsLoaderPill && look.skin;
    const style: Style = pill
      ? { ...look.skin!.parts.loaderPill, fontFamily }
      : { ...look.modalStyle, fontFamily };
    paintRoot(this.modalEl, style, attributes, look.cssVariables);
  }

  private renderOverlayWithLoading(): void {
    // Mount a single host <div> on document.body and attach an open shadow
    // root. All overlay, modal, and stylesheet content lives inside the shadow
    // so the host page's CSS cannot bleed in (and vice versa). This is what
    // makes the SDK render identically across customer sites and WordPress
    // installs that ship aggressive global stylesheets / accessibility plugins.
    this.hostEl = document.createElement("div");
    this.hostEl.setAttribute("data-redacto-consent", "");
    // Lock the host to a zero-size, fixed-position box so it cannot perturb a
    // flex / grid body layout. We deliberately DO NOT reset inherited text
    // properties (font-family, color, line-height) — those flow through the
    // shadow boundary so the modal picks up the host page's font, matching
    // how the React SDK renders.
    this.hostEl.style.position = "fixed";
    this.hostEl.style.top = "0";
    this.hostEl.style.left = "0";
    this.hostEl.style.width = "0";
    this.hostEl.style.height = "0";
    this.hostEl.style.zIndex = "2147483646";
    document.body.appendChild(this.hostEl);
    this.shadowRoot = this.hostEl.attachShadow({ mode: "open" });

    // Lock background scroll on the host page while the modal is open. We
    // capture the previous values so destroy() can restore exactly what was
    // there (including the empty string, which means "use stylesheet rule").
    this.previousBodyOverflow = document.body.style.overflow;
    this.previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // Inject SDK stylesheet into the shadow root (not document.head).
    injectCheckboxStyles(this.shadowRoot);
    injectNoticeFontStyles(this.shadowRoot);

    // Lazy-instantiate TTSManager now that we have a root for it to query.
    this.ttsManager = new TTSManager(
      () => this.handleTalkbackChange(),
      this.shadowRoot
    );

    // A backdrop the console turns off still blocks the page while the host
    // asks for `blockUI`; it only stops dimming it.
    if (this.options.blockUI !== false) {
      this.overlayEl = el("div", { role: "presentation" });
      this.shadowRoot.appendChild(this.overlayEl);
    }

    this.modalEl = el("div", {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": "Privacy consent",
    });
    this.shadowRoot.appendChild(this.modalEl);
    this.renderLoading();
  }

  private renderLoading(): void {
    if (!this.modalEl) return;
    const look = this.lookFor(this.store.getState());
    const fontFamily = resolveNoticeFontFamily({ hostFont: this.options.settings?.font });
    this.paintChrome(look, fontFamily, true);
    clearElement(this.modalEl);
    const { styles } = look;

    if (look.showsLoaderPill && look.skin) {
      this.modalEl.appendChild(
        el(
          "div",
          {
            role: "status",
            "aria-live": "polite",
            "aria-label": "Loading consent notice",
            style: { display: "flex", alignItems: "center", gap: "10px" },
          },
          el("div", { style: styles.loadingSpinner, "aria-hidden": "true" }),
          el("p", { style: { ...look.skin.parts.loaderText, color: look.headingColor } }, "Loading...")
        )
      );
      return;
    }

    const loadingEl = el(
      "div",
      {
        role: "status",
        "aria-live": "polite",
        "aria-label": "Loading consent notice",
        style: styles.loadingContainer,
      },
      el("div", { style: styles.loadingSpinner, "aria-hidden": "true" }),
      el("p", { style: { ...styles.privacyText, color: look.textColor } }, "Loading...")
    );
    this.modalEl.appendChild(
      el("div", { style: { ...styles.content, ...look.responsive.content } }, loadingEl)
    );
  }

  /** The look as the purpose rows and product sections read it. */
  private purposeLook(look: ModalLook, language: string): PurposeLook {
    return {
      styles: look.styles,
      responsive: look.responsive,
      headingColor: look.headingColor,
      textColor: look.textColor,
      mutedTextColor: look.appearance.colors.muted_text,
      selectionControl: look.selectionControl,
      language,
      controlSkin: look.controlSkin,
      rowClassName: look.skin?.parts.rowClassName,
      roomyControls: look.roomyControls,
      drawsSwitches: look.drawsSwitches,
    };
  }

  private disposeSections(): void {
    for (const refs of Object.values(this.sectionRefs)) refs?.dispose();
    this.sectionRefs = {};
  }

  /**
   * Wraps a notice-body section in a disclosure when the layout collapses
   * them. Without SDK labels for the language, the notice's own translated
   * text becomes the title, so nothing shows in English.
   */
  private withNoticeSection(
    section: NoticeSection,
    node: HTMLElement,
    look: ModalLook,
    titles: { preview: string; fallback: string }
  ): HTMLElement {
    if (!look.layout.collapsibleSections) {
      return node;
    }
    const label = sectionLabelsFor(this.store.getState().selectedLanguage)?.[section];
    const refs = renderNoticeAccordion({
      panelId: `privacy-notice-${section}`,
      icon: section === "about" ? "info" : "shield",
      title: label ?? (titles.preview || titles.fallback || ENGLISH_SECTION_LABELS[section]),
      clampTitle: !label,
      hint: label ? titles.preview || undefined : undefined,
      open: this.openSections[section] || this.talkbackActive,
      locked: this.talkbackActive,
      onToggle: () => this.toggleNoticeSection(section),
      styles: look.parts.accordion,
      animate: () => this.animates(),
      content: node,
    });
    this.sectionRefs[section] = refs;
    return refs.element;
  }

  private toggleNoticeSection(section: NoticeSection): void {
    this.openSections = { ...this.openSections, [section]: !this.openSections[section] };
    this.syncSections();
  }

  /** Talkback reads both sections by id, so they stay open while it plays or pauses. */
  private syncSections(): void {
    for (const section of NOTICE_SECTIONS) {
      this.sectionRefs[section]?.setState(
        this.openSections[section] || this.talkbackActive,
        this.talkbackActive
      );
    }
  }

  private handleTalkbackChange(): void {
    const tts = this.ttsManager;
    const active = Boolean(tts && (tts.isPlaying || tts.isPaused));
    if (active !== this.talkbackActive) {
      if (!active) {
        // A section talkback held open would otherwise collapse and unmount
        // the link the visitor tabbed into, dropping focus out of the dialog;
        // such a section is kept open as if the visitor had opened it.
        const focused = this.shadowRoot?.activeElement ?? null;
        for (const section of NOTICE_SECTIONS) {
          if (this.sectionRefs[section]?.holdsFocus(focused)) {
            this.openSections = { ...this.openSections, [section]: true };
          }
        }
      }
      this.talkbackActive = active;
      this.syncSections();
    }
    this.revealPurposeBeingRead();
    this.updateTTSButton();
  }

  /**
   * Expands every rendered copy of the purpose talkback is reading, so the
   * data elements it reads next are on screen rather than collapsed under it.
   */
  private revealPurposeBeingRead(): void {
    const elementId = this.ttsManager?.isPlaying ? this.ttsManager.currentElementId : null;
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    if (!elementId || !content) return;
    const owner = content.purposes.find(
      (purpose) =>
        elementId === `purpose-name-${purpose.uuid}` ||
        elementId === `purpose-description-${purpose.uuid}` ||
        purpose.data_elements.some(
          (element) => elementId === `data-element-${purpose.uuid}-${element.uuid}`
        )
    );
    if (!owner) return;
    const keys = rowsOf(content)
      .filter((row) => row.purpose.uuid === owner.uuid)
      .map((row) => productPurposeKey(row.productUuid, owner.uuid))
      .filter((key) => state.collapsedPurposes[key]);
    if (keys.length === 0) return;
    const collapsedPurposes = { ...state.collapsedPurposes };
    for (const key of keys) collapsedPurposes[key] = false;
    this.store.setState({ collapsedPurposes });
  }

  private rebuildUI(): void {
    const state = this.store.getState();
    if (!state.content || !this.modalEl) return;

    const activeConfig = state.content.detail.active_config;
    const settingsObj = this.options.settings;
    const isMobile = this.mediaQuery.matches;
    const look = this.lookFor(state);
    const fontFamily = resolveNoticeFontFamily({
      hostFont: settingsObj?.font,
      noticeFont: activeConfig.font_preference,
    });
    this.paintChrome(look, fontFamily, false);
    this.disposeSections();
    // Only the notice itself is titled and described by its own copy; the
    // sub-flows keep the dialog's plain label.
    this.modalEl.removeAttribute("aria-labelledby");
    this.modalEl.removeAttribute("aria-describedby");
    const colors = colorsFromLook(look);
    // The sub-flows read the heading and surface colours the way `settings`
    // did, now resolved through the console appearance.
    const subFlowSettings = {
      headingColor: look.headingColor,
      backgroundColor: look.appearance.colors.background,
    };
    const subFlowLogo = activeConfig.logo_url;

    // ─── Age Verification Screen ───
    if (state.showAgeVerification) {
      clearElement(this.modalEl);
      const ageRefs = renderAgeVerification({
        settings: subFlowSettings,
        colors,
        isMobile,
        logoUrl: subFlowLogo,
        onYes: () => this.handleAgeVerificationYes(),
        onNo: () => this.handleAgeVerificationNo(),
        onClose: () => this.handleDecline(),
      });
      this.modalEl.appendChild(ageRefs.container);
      this.keyboardCleanup?.();
      this.keyboardCleanup = setupKeyboardTrap(this.modalEl, () => this.handleDecline());
      return;
    }

    // ─── Guardian Form Screen ───
    if (state.showGuardianForm) {
      clearElement(this.modalEl);
      this.guardianFormRefs = renderGuardianForm({
        formData: state.guardianFormData,
        settings: subFlowSettings,
        colors,
        isMobile,
        logoUrl: subFlowLogo,
        isSubmitting: state.isInitiatingVerification,
        onSubmit: () => this.handleGuardianFormSubmit(),
        onCancel: () => this.handleDecline(),
        onFieldChange: (field, value) => {
          const s = this.store.getState();
          this.store.setState({
            guardianFormData: { ...s.guardianFormData, [field]: value },
          });
        },
      });
      this.modalEl.appendChild(this.guardianFormRefs.container);
      this.keyboardCleanup?.();
      this.keyboardCleanup = setupKeyboardTrap(this.modalEl, () => this.handleDecline());
      return;
    }

    // ─── Verification Screen ───
    if (state.showVerificationScreen) {
      clearElement(this.modalEl);
      const verRefs = renderVerificationScreen({
        settings: subFlowSettings,
        colors,
        isMobile,
        logoUrl: subFlowLogo,
        isInitiating: state.isInitiatingVerification,
        isPolling: state.isPollingStatus,
        isComplete: state.isVerificationComplete,
        error: state.verificationError,
        errorCode: state.verificationErrorCode,
        canRetry: state.canRetryVerification,
        onContinue: () => this.handleVerificationContinue(),
        onBack: (clearName?: boolean) => {
          if (clearName) {
            this.handleChangeGuardian();
          } else if (state.verificationError && state.canRetryVerification) {
            this.handleVerificationRetry();
          } else {
            this.handleDecline();
          }
        },
      });
      this.modalEl.appendChild(verRefs.container);
      this.keyboardCleanup?.();
      this.keyboardCleanup = setupKeyboardTrap(this.modalEl, () => {});
      return;
    }

    // Clear modal content
    clearElement(this.modalEl);
    this.modalEl.setAttribute("aria-labelledby", "privacy-notice-title");
    this.modalEl.setAttribute("aria-describedby", "privacy-notice-description");

    const { styles, responsive, sheetParts, parts, headingColor, textColor } = look;
    const chrome = noticeChromeCopy(state.selectedLanguage);
    const purposeLook = this.purposeLook(look, state.selectedLanguage);
    const linkStyle = { ...styles.link, color: look.linkColor };
    const textStyle = {
      ...styles.privacyText,
      ...responsive.privacyText,
      color: textColor,
    };

    if (look.docked && isMobile) {
      this.modalEl.appendChild(el("div", { style: parts.grabber, "aria-hidden": "true" }));
    }

    // ─── Header: the logo alone on row 1, then title + controls ───
    const logoImg = activeConfig.logo_url
      ? el("img", {
          ...part("logo"),
          src: activeConfig.logo_url,
          style: { ...styles.logo, ...responsive.logo },
          alt: "Logo",
        })
      : null;

    this.noticeTitleEl = el("h2", {
      id: "privacy-notice-title",
      ...part("title"),
      style: { ...styles.title, ...responsive.title, color: headingColor },
    });
    this.noticeTitleEl.textContent = this.getTranslatedText(
      "notice_banner_heading",
      activeConfig.notice_banner_heading || "Privacy Notice"
    );

    // Clean up previous language dropdown (since rebuildUI re-creates it).
    // The dropdown lives inside the shadow root (sibling of the modal) so we
    // remove it from there.
    if (
      this.langSelectorRefs?.dropdown &&
      this.shadowRoot &&
      this.langSelectorRefs.dropdown.parentNode === this.shadowRoot
    ) {
      this.langSelectorRefs.close();
      this.shadowRoot.removeChild(this.langSelectorRefs.dropdown);
    }

    const supportedLangs = Object.keys(activeConfig.supported_languages_and_translations || {});
    const allLanguages = [
      activeConfig.default_language || "English",
      ...supportedLangs.filter((l) => l !== activeConfig.default_language),
    ];

    this.langSelectorRefs = renderLanguageSelector({
      languages: allLanguages,
      selectedLanguage: state.selectedLanguage,
      look: {
        styles,
        responsive,
        chip: look.language.chip,
        menu: look.language.menu,
        selected: look.language.selected,
        rootAttributes: look.rootAttributes,
      },
      onSelect: (lang) => this.handleLanguageSelect(lang),
      dropdownContainer: this.shadowRoot!,
    });

    this.ttsButton = el("button", {
      style: {
        ...styles.topRight,
        ...responsive.topRight,
        ...look.language.chip,
        display: "none",
        padding: "3px 9px",
        alignItems: "center",
        justifyContent: "center",
      },
      type: "button",
      "aria-label": "Start audio playback",
      title: "Start Talkback",
      onClick: () => this.ttsManager?.toggle(),
    });
    this.ttsButton.appendChild(this.speakerIcon(false));

    // Fetch TTS audio (the API uses "English" for English, not "en")
    this.ttsManager?.fetchAudioUrls({
      accessToken: this.options.accessToken,
      baseUrl: this.endpoints.baseUrl,
      ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
      endpointMode: this.endpoints.mode,
      noticeUuid: activeConfig.notice_uuid,
      language: this.getLanguageCode(state.selectedLanguage),
      content: activeConfig,
      purposes: this.talkbackPurposes(state),
      ...this.sandboxParams(),
    });

    // TTS button on LEFT, language selector on RIGHT (matches React)
    const topRight = el("div", {
      style: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
    }, this.ttsButton, this.langSelectorRefs.container);

    const logoPosition = resolveLogoPosition(activeConfig.logo_position);
    const headerRows: HTMLElement[] = [];
    // Row 1 only exists when there is a logo. This package has no bundled
    // fallback (unlike React), so rendering it regardless would leave nothing
    // but the headerStack gap — a phantom band above the title.
    if (logoImg) {
      headerRows.push(
        el(
          "div",
          { style: { ...styles.logoRow, justifyContent: logoRowJustify(logoPosition) } },
          logoImg
        )
      );
    }
    headerRows.push(el("div", { style: styles.headerRow }, this.noticeTitleEl, topRight));

    const topSection = el(
      "div",
      {
        ...part("header"),
        style: {
          ...styles.headerStack,
          ...responsive.headerStack,
          ...sheetParts?.sheetHeader,
          color: textColor,
        },
      },
      ...headerRows
    );

    // ─── Body ───
    this.middleSection = el("div", {
      id: "privacy-notice-description",
      style: {
        ...(sheetParts
          ? sheetParts.sheetBody
          : { ...styles.middleSection, ...responsive.middleSection }),
        color: textColor,
      },
    });

    const noticeTextSpan = el("span", { id: "privacy-notice-text" });
    const noticeText = this.getTranslatedText("notice_text", activeConfig.notice_text);
    noticeTextSpan.textContent = noticeText;

    const policyPrefixText = this.getTranslatedText(
      "privacy_policy_prefix_text",
      activeConfig.privacy_policy_prefix_text || ""
    );
    const policyAnchorText = this.getTranslatedText(
      "privacy_policy_anchor_text",
      activeConfig.privacy_policy_anchor_text || "Privacy Policy"
    );

    this.privacyPolicyLinkEl = el("a", {
      id: "privacy-policy-link",
      href: activeConfig.privacy_policy_url || "#",
      target: "_blank",
      rel: "noopener noreferrer",
      style: linkStyle,
      "aria-label": "Privacy Policy (opens in new tab)",
    }) as HTMLAnchorElement;
    this.privacyPolicyLinkEl.textContent = policyAnchorText;

    const policySpan = el("span", { id: "privacy-policy-text" },
      `${policyPrefixText} `,
      this.privacyPolicyLinkEl
    );

    const productPolicyLinks = getProductPolicyLinks(
      activeConfig.products,
      activeConfig.product_privacy_policies
    );
    const showNoticePolicyLink = shouldShowNoticePolicyLink(
      activeConfig.products,
      productPolicyLinks
    );

    const noticeAndPolicyP = el("p", { ...part("notice-text"), style: textStyle },
      noticeTextSpan,
      showNoticePolicyLink && el("br"),
      showNoticePolicyLink && policySpan
    );

    const dpoPreview = activeConfig.dpo_info
      ? this.getTranslatedDpoText("grievance_text", activeConfig.dpo_info.grievance_text || "")
      : activeConfig.additional_text
        ? this.getTranslatedText("additional_text", activeConfig.additional_text)
        : "";

    this.middleSection.appendChild(
      this.withNoticeSection("about", noticeAndPolicyP, look, {
        preview: noticeText,
        // Without an SDK label or a preview, a section is titled by the
        // notice's own link copy inside it, which the admin translated.
        fallback: activeConfig.privacy_policy_anchor_text ? policyAnchorText : "",
      })
    );

    // Purpose section heading: the notice's own, else the SDK default.
    this.purposeHeadingEl = el("h2", {
      id: "purpose-section-heading",
      style: { ...styles.subTitle, ...responsive.subTitle, color: headingColor },
    });
    this.purposeHeadingEl.textContent = this.getTranslatedText(
      "purpose_section_heading",
      activeConfig.purpose_section_heading || DEFAULT_PURPOSE_SECTION_HEADING
    );
    this.middleSection.appendChild(this.purposeHeadingEl);

    // Wraps the purposes and the DPO block. On a sheet, and wherever the
    // sections collapse into panels, it keeps the body's spacing so those
    // panels never sit flush.
    const purposesGroup = el("div", {
      ...part("purposes"),
      style: sheetParts || look.layout.collapsibleSections ? parts.sheetGroup : null,
    });
    this.middleSection.appendChild(purposesGroup);
    this.selectionsEl = el("div", { style: styles.optionsContainer });
    purposesGroup.appendChild(this.selectionsEl);

    this.productSections = [];
    this.alreadyConsentedPurposeRefs = null;
    const translate = (key: string, defaultText: string, itemId?: string) =>
      this.getTranslatedText(key, defaultText, itemId);
    const soleSection = this.renderSoleProductSection(state, purposeLook);
    const purposesHost = soleSection ? soleSection.body : this.selectionsEl;
    if (soleSection) this.selectionsEl.appendChild(soleSection.container);

    // Already-consented purposes lead, as the reconsent split orders them.
    if (!isMultiProduct(activeConfig.products) && state.alreadyConsentedPurposes.length > 0) {
      this.alreadyConsentedPurposeRefs = renderModalPurposes(state.alreadyConsentedPurposes, {
        selectedPurposes: state.selectedPurposes,
        collapsedPurposes: state.collapsedPurposes,
        selectedDataElements: state.selectedDataElements,
        initialDataElementSelections: state.initialDataElementSelections,
        look: purposeLook,
        isAlreadyConsented: true,
        host: purposesHost,
        getTranslatedText: translate,
        onPurposeToggle: () => {},
        onPurposeCollapse: (uuid) => this.togglePurposeCollapse(uuid),
        onDataElementToggle: () => {},
      });
    }

    if (isMultiProduct(activeConfig.products)) {
      const grouped = renderProductGroups({
        groups: groupsOf(activeConfig),
        products: activeConfig.products,
        selectedPurposes: state.selectedPurposes,
        selectedDataElements: state.selectedDataElements,
        collapsedPurposes: state.collapsedPurposes,
        collapsedProducts: state.collapsedProducts,
        consentedProducts: state.consentedProducts,
        consentedPurposes: state.consentedPurposes,
        initialDataElementSelections: state.initialDataElementSelections,
        recordedAnswers: state.recordedAnswers,
        look: purposeLook,
        host: this.selectionsEl,
        getTranslatedText: translate,
        onPurposeToggle: (uuid, productUuid) => this.handlePurposeCheckboxChange(uuid, productUuid),
        onPurposeCollapse: (uuid, productUuid) => this.togglePurposeCollapse(uuid, productUuid),
        onDataElementToggle: (euuid, puuid, productUuid) =>
          this.handleDataElementCheckboxChange(euuid, puuid, productUuid),
        onToggleCollapse: (productUuid) => this.toggleProductCollapse(productUuid),
        onToggleSelect: (productUuid, choice) => this.handleProductCheckboxChange(productUuid, choice),
      });
      this.purposeRefs = grouped.purposeRefs;
      this.productSections = grouped.sections;
    } else if (state.needsConsentPurposes.length > 0) {
      this.purposeRefs = renderModalPurposes(state.needsConsentPurposes, {
        selectedPurposes: state.selectedPurposes,
        collapsedPurposes: state.collapsedPurposes,
        selectedDataElements: state.selectedDataElements,
        recordedAnswers: state.recordedAnswers,
        look: purposeLook,
        isAlreadyConsented: state.isFullyConsentedReviewMode,
        host: purposesHost,
        getTranslatedText: translate,
        onPurposeToggle: (uuid) => this.handlePurposeCheckboxChange(uuid),
        onPurposeCollapse: (uuid) => this.togglePurposeCollapse(uuid),
        onDataElementToggle: (euuid, puuid) => this.handleDataElementCheckboxChange(euuid, puuid),
      });
    } else {
      this.purposeRefs = null;
    }

    this.mountOtpGate();

    // ─── Additional text, Privacy Center, product policies and DPO ───
    const dpoBlock = this.renderDpoBlock(look, productPolicyLinks, chrome.productPoliciesLabel);
    if (dpoBlock) {
      const centerTitle =
        activeConfig.privacy_center_url && activeConfig.privacy_center_anchor_text
          ? this.getTranslatedText(
              "privacy_center_anchor_text",
              activeConfig.privacy_center_anchor_text
            )
          : "";
      purposesGroup.appendChild(
        this.withNoticeSection("dpo", dpoBlock, look, {
          preview: dpoPreview,
          fallback: centerTitle,
        })
      );
    }

    // Error message
    this.errorEl = el("div", {
      role: "alert",
      style: {
        ...(sheetParts
          ? {
              marginTop: isMobile ? "12px" : "16px",
              marginLeft: isMobile ? "16px" : "24px",
              marginRight: isMobile ? "16px" : "24px",
            }
          : {}),
        backgroundColor: "#FEE2E2",
        color: "#DC2626",
        padding: "12px",
        borderRadius: "6px",
        fontSize: "14px",
        border: "1px solid #FCA5A5",
        display: "none",
      },
    });

    // ─── Footer: three actions ───
    const bottomSection = this.renderFooter(state, look);

    const contentWrapper = el(
      "div",
      { style: sheetParts ? sheetParts.sheetContent : { ...styles.content, ...responsive.content } },
      this.errorEl,
      topSection,
      this.middleSection
    );
    // On a sheet the hint and the actions share one band pinned under the
    // scrolling body, so the hint reads as part of the footer it explains.
    if (sheetParts) {
      contentWrapper.appendChild(el("div", { style: sheetParts.footerBand }, bottomSection));
    } else {
      contentWrapper.appendChild(bottomSection);
    }

    this.modalEl.appendChild(contentWrapper);
    this.bottomSectionEl = bottomSection;
    this.confirmHintEl = renderConfirmHint(styles.confirmHint);

    // Set initial accept button disabled state
    this.updateAcceptButtonState(state);
    this.updateErrorMessage(state);

    // Setup keyboard trap
    this.keyboardCleanup?.();
    this.keyboardCleanup = setupKeyboardTrap(this.modalEl, () => this.handleDecline());
  }

  /** The purposes in the order talkback reads them: as the notice shows them. */
  private talkbackPurposes(state: ModalState): PurposeItemData[] {
    const content = state.content?.detail.active_config;
    if (!content) return [];
    if (!isMultiProduct(content.products) && state.alreadyConsentedPurposes.length > 0) {
      return [...state.alreadyConsentedPurposes, ...state.needsConsentPurposes];
    }
    return purposesInRenderOrder(
      content.products,
      content.purposes,
      content.product_purpose_order
    );
  }

  private renderDpoBlock(
    look: ModalLook,
    productPolicyLinks: ReturnType<typeof getProductPolicyLinks>,
    policiesLabel: string
  ): HTMLElement | null {
    const activeConfig = this.store.getState().content?.detail.active_config;
    if (!activeConfig) return null;
    // The admin's additional text shows with or without a privacy-center
    // link; the link follows it only when the notice has one.
    const hasPrivacyCenterLine = Boolean(
      activeConfig.privacy_center_url || activeConfig.additional_text
    );
    if (!hasPrivacyCenterLine && !activeConfig.dpo_info && productPolicyLinks.length === 0) {
      return null;
    }

    const { styles, responsive } = look;
    const collapsible = look.layout.collapsibleSections;
    const linkStyle = { ...styles.link, color: look.linkColor };
    const footerDiv = el("div", {
      ...part("dpo"),
      style: {
        ...styles.privacyText,
        ...responsive.privacyText,
        color: look.textColor,
        fontSize: responsive.dpoText.fontSize,
        lineHeight: "1.4",
        // Inside a disclosure the panel is the separator, and the copy is
        // what the visitor opened it to read.
        ...(collapsible
          ? {}
          : {
              borderTop: "1px solid rgba(152, 162, 179, 0.3)",
              paddingTop: "12px",
              marginTop: "12px",
            }),
      },
    });

    const footerP = el("p", { style: { margin: "0", opacity: collapsible ? "1" : "0.6" } });

    if (hasPrivacyCenterLine) {
      if (activeConfig.additional_text) {
        const addlSpan = el("span", { id: "additional-text" });
        addlSpan.textContent = this.getTranslatedText("additional_text", activeConfig.additional_text);
        footerP.appendChild(addlSpan);
      }
      if (activeConfig.privacy_center_url) {
        if (activeConfig.additional_text) footerP.appendChild(document.createTextNode(" "));
        const pcAnchorText = this.getTranslatedText(
          "privacy_center_anchor_text",
          activeConfig.privacy_center_anchor_text || "Privacy Center"
        );
        this.privacyCenterLinkEl = el("a", {
          id: "privacy-center-link",
          href: activeConfig.privacy_center_url,
          target: "_blank",
          rel: "noopener noreferrer",
          style: linkStyle,
        }) as HTMLAnchorElement;
        this.privacyCenterLinkEl.textContent = pcAnchorText;
        footerP.appendChild(this.privacyCenterLinkEl);
        footerP.appendChild(document.createTextNode("."));
      }
    }

    if (productPolicyLinks.length > 0) {
      if (hasPrivacyCenterLine) {
        footerP.appendChild(el("br"));
      }
      // Each policy is named the way its product heading is: translated.
      footerP.appendChild(
        renderProductPolicyLine({
          label: this.getTranslatedText("product_privacy_policies_label", policiesLabel),
          links: productPolicyLinks.map((link) => ({
            ...link,
            name: this.getTranslatedText("products.name", link.name, link.uuid),
          })),
          linkStyle,
        })
      );
    }

    if (activeConfig.dpo_info) {
      const dpo = activeConfig.dpo_info;

      if (hasPrivacyCenterLine || productPolicyLinks.length > 0) {
        footerP.appendChild(el("br"));
      }

      // Grievance line
      if (dpo.grievance_text) {
        const gText = el("span", { id: "dpo-grievance-text" });
        gText.textContent = this.getTranslatedDpoText("grievance_text", dpo.grievance_text);
        footerP.appendChild(gText);
        footerP.appendChild(document.createTextNode(" "));

        if (dpo.grievance_url) {
          const gLink = el("a", {
            id: "dpo-grievance-link",
            href: dpo.grievance_url,
            target: "_blank",
            rel: "noopener noreferrer",
            style: linkStyle,
          });
          gLink.textContent = this.getTranslatedDpoText(
            "grievance_anchor_text",
            dpo.grievance_anchor_text || "click here"
          );
          footerP.appendChild(gLink);
        }

        if (dpo.grievance_email) {
          footerP.appendChild(document.createTextNode(" "));
          const connectorSpan = el("span", { id: "dpo-grievance-email-connector" });
          connectorSpan.textContent = this.getTranslatedDpoText(
            "grievance_email_connector_text",
            dpo.grievance_email_connector_text || "or email to"
          );
          footerP.appendChild(connectorSpan);
          footerP.appendChild(document.createTextNode(" "));

          const emailLink = el("a", {
            id: "dpo-grievance-email",
            href: `mailto:${dpo.grievance_email}`,
            style: linkStyle,
          });
          emailLink.textContent = dpo.grievance_email;
          footerP.appendChild(emailLink);
        }
      }

      // DP Board line
      if (dpo.dp_board_text) {
        footerP.appendChild(el("br"));
        const dpText = el("span", { id: "dpo-dp-board-text" });
        dpText.textContent = this.getTranslatedDpoText("dp_board_text", dpo.dp_board_text);
        footerP.appendChild(dpText);
        footerP.appendChild(document.createTextNode(" "));

        if (dpo.dp_board_url) {
          const dpLink = el("a", {
            id: "dpo-dp-board-link",
            href: dpo.dp_board_url,
            target: "_blank",
            rel: "noopener noreferrer",
            style: linkStyle,
          });
          dpLink.textContent = this.getTranslatedDpoText(
            "dp_board_anchor_text",
            dpo.dp_board_anchor_text || "here"
          );
          footerP.appendChild(dpLink);
        }
      }

      // DPO line: the console links the DPO by email; a URL is the fallback.
      if (dpo.dpo_text) {
        footerP.appendChild(el("br"));
        const dpoTextSpan = el("span", { id: "dpo-dpo-text" });
        dpoTextSpan.textContent = this.getTranslatedDpoText("dpo_text", dpo.dpo_text);
        footerP.appendChild(dpoTextSpan);
        footerP.appendChild(document.createTextNode(" "));

        const dpoHref = dpo.dpo_email ? `mailto:${dpo.dpo_email}` : dpo.dpo_url;
        if (dpoHref) {
          const dpoLink = el("a", {
            id: "dpo-dpo-link",
            href: dpoHref,
            ...(dpo.dpo_email ? {} : { target: "_blank", rel: "noopener noreferrer" }),
            style: linkStyle,
          });
          dpoLink.textContent = this.getTranslatedDpoText(
            "dpo_anchor_text",
            dpo.dpo_anchor_text || "Click here"
          );
          footerP.appendChild(dpoLink);
        }
      }
    }

    footerDiv.appendChild(footerP);
    return footerDiv;
  }

  /**
   * The notice's three actions.
   *
   *   wide:   [Decline] <-- gap --> [Accept Selected] [Accept All]
   *   narrow: [Accept All] / [Accept Selected] / [Decline], stacked full width
   *   paired: [Accept All] / [Accept Selected] [Decline]  (narrow, paired footer)
   *
   * The children are appended in a different order per breakpoint rather than
   * reordered with CSS, so focus order follows visual order (WCAG 2.4.3).
   */
  private renderFooter(state: ModalState, look: ModalLook): HTMLElement {
    const activeConfig = state.content!.detail.active_config;
    const isMobile = this.mediaQuery.matches;
    const { styles, responsive, sheetParts } = look;
    const review = state.isFullyConsentedReviewMode;

    const containerStyle: Style = {
      ...(review
        ? styles.bottomSection
        : isMobile
          ? styles.actionButtonStack
          : styles.actionButtonRow),
      // The band pads the footer; the buttons' own inset would double it.
      ...(sheetParts ? { paddingTop: 0 } : {}),
      color: look.textColor,
    };
    const bottomSection = el("div", { ...part("footer"), style: containerStyle });

    const baseButton: Style = {
      ...styles.button,
      ...responsive.button,
      ...(review ? {} : isMobile ? styles.stackButton : styles.rowButton),
    };

    // Review mode collapses to the single host-supplied continue button.
    if (review) {
      this.acceptBtn = el("button", {
        id: ACCEPT_SELECTED_BUTTON_ID,
        type: "button",
        style: { ...baseButton, ...styles.acceptButton, ...look.buttons.acceptAll },
        onClick: () => this.confirmFirst("accept-selected", () => this.handleAccept("selected")),
      }) as HTMLButtonElement;
      this.acceptBtn.textContent = this.options.reviewModeButtonText || "Continue";
      this.acceptAllBtn = null;
      this.declineBtn = null;
      bottomSection.appendChild(this.acceptBtn);
      return bottomSection;
    }

    this.acceptAllBtn = el("button", {
      id: ACCEPT_ALL_BUTTON_ID,
      ...part("accept-all"),
      type: "button",
      style: { ...baseButton, ...styles.acceptButton, ...look.buttons.acceptAll },
      onClick: () => this.confirmFirst("accept-all", () => this.handleAccept("all")),
    }) as HTMLButtonElement;
    this.acceptAllBtn.textContent = this.getTranslatedText(
      "accept_all_button_text",
      activeConfig.accept_all_button_text || DEFAULT_ACCEPT_ALL_BUTTON_TEXT
    );

    this.acceptBtn = el("button", {
      id: ACCEPT_SELECTED_BUTTON_ID,
      ...part("accept-selected"),
      type: "button",
      style: { ...baseButton, ...styles.acceptSecondaryButton, ...look.buttons.acceptSelected },
      onClick: () => this.confirmFirst("accept-selected", () => this.handleAccept("selected")),
    }) as HTMLButtonElement;
    this.acceptBtn.textContent = this.getTranslatedText(
      "confirm_button_text",
      activeConfig.confirm_button_text || DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT
    );

    this.declineBtn = el("button", {
      id: DECLINE_BUTTON_ID,
      ...part("decline"),
      type: "button",
      style: {
        ...baseButton,
        ...styles.cancelButton,
        ...look.buttons.decline,
        // Only the wide row needs the spacer that pushes the accepts right.
        ...(isMobile ? {} : styles.declineSpacer),
      },
      onClick: () => this.confirmFirst("decline", () => this.handleDecline()),
    }) as HTMLButtonElement;
    this.declineBtn.textContent = this.getTranslatedText(
      "decline_button_text",
      activeConfig.decline_button_text || DEFAULT_DECLINE_BUTTON_TEXT
    );

    if (isMobile && look.layout.pairedFooter) {
      bottomSection.appendChild(this.acceptAllBtn);
      bottomSection.appendChild(
        el("div", { style: look.parts.actionButtonPair }, this.acceptBtn, this.declineBtn)
      );
    } else if (isMobile) {
      bottomSection.appendChild(this.acceptAllBtn);
      bottomSection.appendChild(this.acceptBtn);
      bottomSection.appendChild(this.declineBtn);
    } else {
      bottomSection.appendChild(this.declineBtn);
      bottomSection.appendChild(this.acceptBtn);
      bottomSection.appendChild(this.acceptAllBtn);
    }
    return bottomSection;
  }
  private getLanguageCode(displayLanguage: string): string {
    const content = this.store.getState().content?.detail.active_config;
    if (!content) return "English";

    // English maps to "English" for audio API
    if (displayLanguage === "English" || displayLanguage === "en" || displayLanguage === "EN") {
      return "English";
    }

    // If display language exists in translations, use it directly
    if (content.supported_languages_and_translations?.[displayLanguage]) {
      return displayLanguage;
    }

    // Fallback to default language or English
    return content.default_language === "en" || content.default_language === "EN"
      ? "English"
      : content.default_language || "English";
  }

  private getTranslatedDpoText(key: string, defaultText: string): string {
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    if (!content) return defaultText;

    const lang = state.selectedLanguage;
    if (lang === "English" || lang === "en" || lang === "EN") return defaultText;

    const translations = content.supported_languages_and_translations;
    if (!translations) return defaultText;

    const translationMap = translations[lang];
    if (!translationMap?.dpo_info) return defaultText;

    const value = (translationMap.dpo_info as any)[key];
    return typeof value === "string" && value ? value : defaultText;
  }

  private renderError(message: string): void {
    if (!this.modalEl) return;

    clearElement(this.modalEl);

    const errorContent = el(
      "div",
      { style: { ...styles.content, alignItems: "center", justifyContent: "center" } },
      el("div", {
        role: "alert",
        style: {
          backgroundColor: "#FEE2E2",
          color: "#DC2626",
          padding: "16px",
          borderRadius: "8px",
          fontSize: "14px",
          border: "1px solid #FCA5A5",
          textAlign: "center",
          maxWidth: "400px",
        },
      }, message)
    );

    this.modalEl.appendChild(errorContent);
  }

  private getTranslatedText(key: string, defaultText: string, itemId?: string): string {
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    if (!content) return defaultText;

    const lang = state.selectedLanguage;
    if (lang === "English" || lang === "en" || lang === "EN") {
      if (key === "privacy_policy_anchor_text" && content.privacy_policy_anchor_text) {
        return content.privacy_policy_anchor_text;
      }
      return defaultText;
    }

    const translations = content.supported_languages_and_translations;
    if (!translations) return defaultText;

    const translationMap = translations[lang] as TranslationObject | undefined;
    if (!translationMap) return defaultText;

    if (itemId) {
      if (key === "purposes.name" || key === "purposes.description") {
        const purposeData = translationMap.purposes?.[itemId];
        if (purposeData) {
          if (typeof purposeData === "string") {
            return key === "purposes.name" ? purposeData : defaultText;
          }
          return key === "purposes.name"
            ? purposeData.name || defaultText
            : purposeData.description || defaultText;
        }
        return defaultText;
      }
      if (key === "products.name" || key === "products.description") {
        const productData = translationMap.products?.[itemId];
        if (productData) {
          if (typeof productData === "string") {
            return key === "products.name" ? productData : defaultText;
          }
          return key === "products.name"
            ? productData.name || defaultText
            : productData.description || defaultText;
        }
        return defaultText;
      }
      if (key.startsWith("data_elements.")) {
        return translationMap.data_elements?.[itemId] || defaultText;
      }
    }

    const value = (translationMap as any)[key];
    if (typeof value === "string" && value) return value;
    return defaultText;
  }

  private handleLanguageSelect(lang: string): void {
    this.store.setState({ selectedLanguage: lang });
    // Rebuild UI to update all translations
    this.rebuildUI();
  }

  private togglePurposeCollapse(purposeUuid: string, productUuid?: string): void {
    const state = this.store.getState();
    const key = productPurposeKey(productUuid, purposeUuid);
    this.store.setState({
      collapsedPurposes: {
        ...state.collapsedPurposes,
        [key]: !state.collapsedPurposes[key],
      },
    });
  }

  private handlePurposeCheckboxChange(purposeUuid: string, productUuid?: string): void {
    this.otpSession.dropStashedAccept();
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    if (!content) return;

    const purpose = content.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose) return;

    const next = purposeToggle(purpose, productUuid, state.selectedPurposes);
    this.store.setState({
      selectedPurposes: { ...state.selectedPurposes, ...next.selectedPurposes },
      selectedDataElements: { ...state.selectedDataElements, ...next.selectedDataElements },
    });
  }

  private handleDataElementCheckboxChange(
    elementUuid: string,
    purposeUuid: string,
    productUuid?: string
  ): void {
    this.otpSession.dropStashedAccept();
    const state = this.store.getState();
    const content = state.content?.detail.active_config;
    if (!content) return;

    const purpose = content.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose) return;

    const key = productPurposeKey(productUuid, purposeUuid);
    const combinedId = `${key}-${elementUuid}`;
    const newDataElements = {
      ...state.selectedDataElements,
      [combinedId]: !state.selectedDataElements[combinedId],
    };

    this.store.setState({
      selectedDataElements: newDataElements,
      selectedPurposes: {
        ...state.selectedPurposes,
        [key]: purposeCheckedFromElements(purpose, productUuid, newDataElements),
      },
    });
  }

  private toggleProductCollapse(productUuid: string): void {
    const state = this.store.getState();
    this.store.setState({
      collapsedProducts: {
        ...state.collapsedProducts,
        [productUuid]: !state.collapsedProducts[productUuid],
      },
    });
  }

  private isRowLocked(state: ModalState, purposeUuid: string, productUuid?: string): boolean {
    if (isMultiProduct(state.content?.detail.active_config.products)) {
      return state.consentedPurposes[productPurposeKey(productUuid, purposeUuid)] ?? false;
    }
    return state.alreadyConsentedPurposes.some((purpose) => purpose.uuid === purposeUuid);
  }

  private handleProductCheckboxChange(productUuid: string, choice?: boolean): void {
    this.otpSession.dropStashedAccept();
    const state = this.store.getState();
    const section = this.productSections.find((refs) => refs.productUuid === productUuid);
    if (!section) return;

    const editable = section.boxPurposes.filter(
      (purpose) => !this.isRowLocked(state, purpose.uuid, section.keyProductUuid)
    );
    if (editable.length === 0) return;

    const next = sectionToggle(editable, section.keyProductUuid, state.selectedPurposes, choice);
    this.store.setState({
      selectedPurposes: { ...state.selectedPurposes, ...next.selectedPurposes },
      selectedDataElements: { ...state.selectedDataElements, ...next.selectedDataElements },
    });
  }

  private renderSoleProductSection(state: ModalState, look: PurposeLook): ProductSectionRefs | null {
    const content = state.content?.detail.active_config;
    const products = content?.products;
    if (!content || !products || products.length !== 1) return null;

    const product = products[0];
    const section = renderProductSection({
      product,
      purposes: content.purposes,
      selectedPurposes: state.selectedPurposes,
      collapsed: state.collapsedProducts[product.uuid] || false,
      locked: state.consentedProducts[product.uuid] ?? state.isFullyConsentedReviewMode,
      isRequired: productIsRequired(products, product.uuid),
      look,
      getTranslatedText: (key, defaultText, itemId) => this.getTranslatedText(key, defaultText, itemId),
      onToggleCollapse: (productUuid) => this.toggleProductCollapse(productUuid),
      onToggleSelect: (productUuid, choice) => this.handleProductCheckboxChange(productUuid, choice),
    });
    this.productSections = [section];
    return section;
  }

  private updateProductSections(state: ModalState): void {
    for (const section of this.productSections) {
      updateProductSection(
        section,
        state.selectedPurposes,
        state.collapsedProducts[section.productUuid] || false
      );
    }
  }

  /**
   * Shared by all three accept buttons. The mode only decides which
   * purposes/data elements are submitted — everything downstream, including
   * the host's `onAccept` callback, is identical.
   */
  /**
   * Shared by all three accept buttons. The mode only decides which
   * purposes/data elements are submitted — everything downstream, including
   * the host's `onAccept` callback, is identical.
   */
  private async handleAccept(mode: AcceptMode = "selected"): Promise<void> {
    const state = this.store.getState();
    if (state.isSubmitting) return;

    // Review mode — just close
    if (state.isFullyConsentedReviewMode) {
      this.options.onAccept();
      this.destroy();
      return;
    }

    // Block submission for minors who have not completed guardian verification
    if (state.isMinorFlow && !state.verificationReference && !state.selfDeclaredAdult) {
      this.store.setState({
        errorMessage: "Guardian verification is required before consent can be submitted.",
      });
      return;
    }

    const content = state.content?.detail.active_config;
    if (!content) return;
    const rows = rowsOf(content);

    if (this.otpSession.wouldHold(this.options.otpGate)) {
      // The rows stay visible behind the code entry, so they show what will
      // be recorded: a shortcut's selection while it is pending, and the
      // visitor's own ticks again once Accept Selected re-pends.
      if (mode === "selected") {
        const own = this.preGateSelection;
        if (own) {
          this.preGateSelection = null;
          this.store.setState(own);
        }
        if (!this.confirmVerdict(this.store.getState()).ok) {
          if (this.otpSession.isLocked) this.holdForOtp(mode);
          return;
        }
      } else {
        this.preGateSelection ??= {
          selectedPurposes: state.selectedPurposes,
          selectedDataElements: state.selectedDataElements,
        };
        this.store.setState(
          buildSelectionForMode(rows, mode, {
            selectedPurposes: state.selectedPurposes,
            selectedDataElements: state.selectedDataElements,
          })
        );
      }
      this.holdForOtp(mode);
      return;
    }

    if (mode === "selected" && !this.confirmVerdict(state).ok) return;

    const selection = buildSelectionForMode(rows, mode, {
      selectedPurposes: state.selectedPurposes,
      selectedDataElements: state.selectedDataElements,
    });
    // Reflect the shortcut's selection in the checkboxes so the notice shows
    // what was sent if the request fails and it stays open.
    if (mode !== "selected") {
      this.store.setState({
        selectedPurposes: selection.selectedPurposes,
        selectedDataElements: selection.selectedDataElements,
      });
    }

    this.abortController = new AbortController();
    this.store.setState({ isSubmitting: true, isSubmittingAccept: true, errorMessage: null });

    try {
      const purposes = submissionPurposes(rows, selection);

      await submitConsentEvent({
        accessToken: this.options.accessToken,
        baseUrl: this.endpoints.baseUrl,
        ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
        noticeUuid: state.content!.detail.uuid,
        purposes,
        declined: false,
        meta_data: this.options.applicationId ? { specific_uuid: this.options.applicationId } : undefined,
        guardianVerificationReference: state.verificationReference || undefined,
        selfDeclaredAdult: state.selfDeclaredAdult || undefined,
        language: state.selectedLanguage,
        signal: this.abortController.signal,
        ...this.sandboxParams(),
      });

      this.options.onAccept();
      this.destroy();
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      const error = err as Error;
      const message = error.message || "Failed to submit consent. Please try again.";
      this.store.setState({ errorMessage: message, isSubmitting: false, isSubmittingAccept: false });
      this.options.onError?.(error);
    }
  }
  private handleDecline(): void {
    this.otpSession.dropStashedAccept();
    const state = this.store.getState();
    if (state.isSubmitting) return;

    // Stop any in-flight verification polling and clear the persisted
    // DigiLocker session so the next open starts the flow from scratch
    // (age verification → guardian form → verification screen).
    this.stopPolling();
    clearConsentState();

    this.options.onDecline();
    this.destroy();
  }

  private updateCheckboxes(state: ModalState): void {
    if (this.purposeRefs) {
      for (const [key, control] of this.purposeRefs.purposeControls) {
        control.setChecked(state.selectedPurposes[key] || false);
      }
      for (const [key, control] of this.purposeRefs.elementControls) {
        control.setChecked(state.selectedDataElements[key] || false);
      }
    }
    this.updateProductSections(state);
    // Update accept button disabled state
    this.updateAcceptButtonState(state);
  }

  private confirmVerdict(state: ModalState): ConfirmVerdict {
    const content = state.content?.detail.active_config;
    if (!content) return { ok: false, reason: "nothing-chosen", blocking: [] };
    return noticeConfirmVerdict(
      content,
      state.selectedPurposes,
      state.recordedPurposeSelections
    );
  }

  private updateConfirmHint(state: ModalState, verdict: ConfirmVerdict): void {
    if (!this.confirmHintEl || !this.bottomSectionEl) return;
    const submitting = state.isSubmittingAccept || state.isSubmittingDecline;
    const chrome = noticeChromeCopy(state.selectedLanguage);
    const text =
      submitting || state.isFullyConsentedReviewMode
        ? null
        : confirmHint(
            verdict,
            state.content?.detail.active_config.products,
            (product) => this.getTranslatedText("products.name", product.name, product.uuid),
            {
              ...CONFIRM_HINT_COPY,
              "nothing-chosen": chrome.hintNothingChosen,
              "nothing-engaged": chrome.hintNothingEngaged,
              "required-outstanding": chrome.hintRequiredOutstanding,
            }
          );
    updateConfirmHint(this.confirmHintEl, this.bottomSectionEl, this.acceptBtn, text);
  }
  private updateAcceptButtonState(state: ModalState): void {
    if (!this.acceptBtn || state.isFullyConsentedReviewMode) return;

    const submitting = state.isSubmittingAccept || state.isSubmittingDecline;
    const verdict = this.confirmVerdict(state);
    this.updateConfirmHint(state, verdict);
    // Only "accept selected" waits on the required boxes — sparing the two
    // shortcuts that gate is the point of the feature.
    const setDisabled = (btn: HTMLButtonElement | null, disabled: boolean) => {
      if (!btn) return;
      btn.disabled = disabled;
      btn.style.opacity = disabled ? "0.5" : "1";
      btn.style.cursor = disabled ? "not-allowed" : "pointer";
    };

    setDisabled(this.acceptBtn, !verdict.ok || submitting);
    setDisabled(this.acceptAllBtn, submitting);
  }

  private updateCollapseState(state: ModalState): void {
    const updateRefs = (refs: ModalPurposeRefs | null) => {
      if (!refs) return;
      for (const [purposeUuid, container] of refs.dataElementContainers) {
        const isCollapsed = state.collapsedPurposes[purposeUuid];

        container.style.display = !isCollapsed && container.childElementCount > 0 ? "flex" : "none";

        const chevron = refs.chevrons.get(purposeUuid);
        if (chevron) {
          (chevron as unknown as HTMLElement).style.transform = !isCollapsed ? "rotate(90deg)" : "rotate(0deg)";
        }

        const collapseBtn = refs.collapseButtons.get(purposeUuid);
        if (collapseBtn) {
          collapseBtn.setAttribute("aria-expanded", String(!isCollapsed));
        }
      }
    };

    updateRefs(this.purposeRefs);
    updateRefs(this.alreadyConsentedPurposeRefs);
  }

  private updateErrorMessage(state: ModalState): void {
    if (!this.errorEl) return;
    if (state.errorMessage) {
      this.errorEl.textContent = state.errorMessage;
      this.errorEl.style.display = "block";
    } else {
      this.errorEl.style.display = "none";
    }
  }

  // ─── Age Verification Handlers ───

  private handleAgeVerificationYes(): void {
    this.store.setState({
      isMinorFlow: false,
      selfDeclaredAdult: true,
      showAgeVerification: false,
    });
  }

  private handleAgeVerificationNo(): void {
    this.store.setState({
      showAgeVerification: false,
      showGuardianForm: true,
    });
  }

  // ─── Guardian Form Handlers ───

  private async handleGuardianFormSubmit(): Promise<void> {
    const state = this.store.getState();
    const errors = validateGuardianForm(state.guardianFormData);
    if (Object.keys(errors).length > 0) {
      if (this.guardianFormRefs) {
        if (errors.guardianName) {
          this.guardianFormRefs.nameError.textContent = errors.guardianName;
          this.guardianFormRefs.nameError.style.display = "block";
        }
        if (errors.guardianContact) {
          this.guardianFormRefs.contactError.textContent = errors.guardianContact;
          this.guardianFormRefs.contactError.style.display = "block";
        }
        if (errors.guardianRelationship) {
          this.guardianFormRefs.relationshipError.textContent = errors.guardianRelationship;
          this.guardianFormRefs.relationshipError.style.display = "block";
        }
      }
      return;
    }

    this.store.setState({ isInitiatingVerification: true });

    const digilockerMode = this.options.digilockerMode || "redirect";
    const callbackUrl = digilockerMode === "popup"
      ? new URL(this.options.digilockerCallbackUrl || "/digilocker-callback", window.location.origin).href
      : undefined;

    try {
      const response = await initiateGuardianVerification({
        accessToken: this.options.accessToken,
        baseUrl: this.endpoints.baseUrl,
        guardianName: state.guardianFormData.guardianName,
        guardianContact: state.guardianFormData.guardianContact,
        guardianRelationship: state.guardianFormData.guardianRelationship,
        frontendCallbackUrl: callbackUrl,
      });

      if (response.already_verified && response.verification_reference) {
        this.store.setState({
          verificationReference: response.verification_reference,
          showGuardianForm: false,
          showVerificationScreen: true,
          isInitiatingVerification: false,
          isVerificationComplete: true,
        });
        // Auto-transition to consent form after 1s
        setTimeout(() => {
          this.store.setState({
            showVerificationScreen: false,
            isVerificationComplete: false,
          });
        }, 1000);
        return;
      }

      // Save state for redirect flow
      saveConsentState({
        flowState: "digilocker-pending",
        sessionToken: response.session_token,
        guardianInfoUuid: response.guardian_info_uuid,
        selectedPurposes: state.selectedPurposes,
        selectedDataElements: state.selectedDataElements,
        timestamp: new Date().toISOString(),
        originalUrl: window.location.href,
        noticeId: this.options.noticeId,
        baseUrl: this.endpoints.baseUrl,
      });

      // Show verification screen with "Opening DigiLocker..." state
      this.store.setState({
        showGuardianForm: false,
        showVerificationScreen: true,
        isInitiatingVerification: true,
        isPollingStatus: false,
      });

      if (digilockerMode === "popup" && response.digilocker_redirect_url) {
        const popup = window.open(
          response.digilocker_redirect_url,
          "digilocker_verification",
          "width=600,height=700,scrollbars=yes,resizable=yes"
        );
        if (popup) {
          this.verificationPopup = popup;
          this.startPopupMonitoring(popup);
        } else {
          this.store.setState({
            isInitiatingVerification: false,
            verificationError: "Popup blocked. Please allow popups for this site.",
            canRetryVerification: true,
          });
          return;
        }
      } else if (response.digilocker_redirect_url) {
        const openedWindow = window.open(response.digilocker_redirect_url, "_blank");
        if (!openedWindow) {
          this.store.setState({
            isInitiatingVerification: false,
            verificationError: "Unable to open DigiLocker. Please allow popups.",
            canRetryVerification: true,
          });
          return;
        }
        try { openedWindow.opener = null; } catch {}
      }

      // Switch to polling state (spinner + "Please complete verification..." message)
      this.store.setState({
        isInitiatingVerification: false,
        isPollingStatus: true,
      });
      this.startStatusPolling(response.session_token!);
    } catch (error) {
      this.store.setState({ isInitiatingVerification: false });
      const err = error as Error & { status?: number };
      if (this.guardianFormRefs) {
        this.guardianFormRefs.generalError.textContent = err.message || "Failed to initiate verification.";
        this.guardianFormRefs.generalError.style.display = "block";
      }
    }
  }

  // ─── Verification Polling ───

  private startStatusPolling(token: string): void {
    this.verificationSessionToken = token;
    let attempts = 0;
    let consecutiveErrors = 0;
    let paused = false;
    const MAX_ATTEMPTS = 40;
    const MAX_CONSECUTIVE_ERRORS = 3;
    const POLL_INTERVAL = 3000;

    const poll = async () => {
      if (paused) return;
      attempts++;

      if (attempts > MAX_ATTEMPTS) {
        this.stopPolling();
        this.store.setState({
          verificationError: "Verification is taking longer than expected. Please try again.",
          verificationErrorCode: "SESSION_EXPIRED",
          canRetryVerification: true,
          isPollingStatus: false,
        });
        return;
      }

      try {
        const response = await verifyGuardianStatus({
          accessToken: this.options.accessToken,
          baseUrl: this.endpoints.baseUrl,
          sessionToken: token,
        });

        consecutiveErrors = 0;

        if (response.status === "verified") {
          this.stopPolling();
          const ref = response.verification_reference || response.guardian_details?.verification_reference;
          if (!ref) {
            this.store.setState({
              verificationError: "Verification completed but no reference received. Please try again.",
              canRetryVerification: true,
              isPollingStatus: false,
            });
            return;
          }
          this.store.setState({
            verificationReference: ref,
            isVerificationComplete: true,
            isPollingStatus: false,
          });
          clearConsentState();
          return;
        }

        if (response.status === "failed" || response.status === "expired") {
          this.stopPolling();
          this.store.setState({
            verificationError: getVerificationErrorMessage(response.error_code, response.error),
            verificationErrorCode: response.error_code,
            canRetryVerification: response.can_retry,
            isPollingStatus: false,
          });
          return;
        }

        if (!paused) {
          this.statusPollTimeout = setTimeout(poll, POLL_INTERVAL);
        }
      } catch (error) {
        const err = error as Error & { status?: number };
        if (err.status === 404 || err.status === 410) {
          this.stopPolling();
          this.store.setState({
            verificationError: "Verification session not found or expired.",
            verificationErrorCode: "SESSION_EXPIRED",
            canRetryVerification: true,
            isPollingStatus: false,
          });
          return;
        }

        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          this.stopPolling();
          this.store.setState({
            verificationError: err.message || "Unable to check verification status.",
            canRetryVerification: true,
            isPollingStatus: false,
          });
          return;
        }

        this.statusPollTimeout = setTimeout(poll, POLL_INTERVAL);
      }
    };

    // Pause when tab hidden, resume on return
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        paused = true;
        if (this.statusPollTimeout) {
          clearTimeout(this.statusPollTimeout);
          this.statusPollTimeout = null;
        }
      } else {
        paused = false;
        poll();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);

    this.statusPollTimeout = setTimeout(poll, POLL_INTERVAL);
  }

  private stopPolling(): void {
    if (this.statusPollTimeout) {
      clearTimeout(this.statusPollTimeout);
      this.statusPollTimeout = null;
    }
    if (this.popupCheckInterval) {
      clearInterval(this.popupCheckInterval);
      this.popupCheckInterval = null;
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    this.verificationPopup?.close();
    this.verificationPopup = null;
  }

  private startPopupMonitoring(popup: Window): void {
    this.popupCheckInterval = setInterval(() => {
      if (popup.closed) {
        if (this.popupCheckInterval) {
          clearInterval(this.popupCheckInterval);
          this.popupCheckInterval = null;
        }
      }
    }, 500);
  }

  // ─── Verification Screen Handlers ───

  private handleVerificationContinue(): void {
    this.store.setState({
      showVerificationScreen: false,
      isVerificationComplete: false,
    });
  }

  private handleVerificationRetry(): void {
    this.store.setState({
      showVerificationScreen: false,
      showGuardianForm: true,
      verificationError: null,
      verificationErrorCode: null,
      canRetryVerification: false,
      isVerificationComplete: false,
    });
  }

  private handleChangeGuardian(): void {
    this.store.setState({
      showVerificationScreen: false,
      showGuardianForm: true,
      verificationError: null,
      verificationErrorCode: null,
      canRetryVerification: false,
      isVerificationComplete: false,
      guardianFormData: { guardianName: "", guardianContact: "", guardianRelationship: "" },
    });
  }

  // ─── TTS ───

  /** The talkback control's glyph: a speaker, or a pause while it plays. */
  private speakerIcon(playing: boolean): SVGElement {
    const stroke = this.look?.appearance.colors.language_text || "currentColor";
    const attrs = {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke,
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
    };
    return playing
      ? svg(
          "svg",
          attrs,
          svg("rect", { x: "6", y: "4", width: "4", height: "16" }),
          svg("rect", { x: "14", y: "4", width: "4", height: "16" })
        )
      : svg(
          "svg",
          attrs,
          svg("polygon", { points: "11 5 6 9 2 9 2 15 6 15 11 19 11 5" }),
          svg("path", { d: "M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" })
        );
  }

  private updateTTSButton(): void {
    if (!this.ttsButton || !this.ttsManager) return;

    // When the current language has no TTS audio (e.g. Santali in some
    // configs), hide the speaker icon entirely. Without this branch the
    // button would keep whatever display value it had from the previous
    // language and clicking it would replay the previous language's audio.
    if (!this.ttsManager.isAvailable) {
      this.ttsButton.style.display = "none";
      return;
    }

    this.ttsButton.style.display = "flex";
    clearElement(this.ttsButton);
    const playing = this.ttsManager.isPlaying;
    this.ttsButton.appendChild(this.speakerIcon(playing));

    if (playing) {
      this.ttsButton.setAttribute("aria-label", "Pause audio playback");
      this.ttsButton.setAttribute("title", "Pause Talkback");
    } else {
      this.ttsButton.setAttribute("aria-label", this.ttsManager.isPaused ? "Resume audio playback" : "Start audio playback");
      this.ttsButton.setAttribute("title", this.ttsManager.isPaused ? "Resume Talkback" : "Start Talkback");
    }
  }

  private updateButtonStates(state: ModalState): void {
    const content = state.content?.detail.active_config;

    // All three accept buttons submit through the same path, so a submission
    // in flight busies every one of them.
    const applySubmittingState = (
      btn: HTMLButtonElement | null,
      idleLabel: string
    ) => {
      if (!btn) return;
      btn.textContent = state.isSubmittingAccept
        ? SUBMITTING_BUTTON_TEXT
        : idleLabel;
      btn.disabled = state.isSubmittingAccept;
      btn.style.opacity = state.isSubmittingAccept ? "0.7" : "1";
      btn.style.cursor = state.isSubmittingAccept ? "not-allowed" : "pointer";
    };

    applySubmittingState(
      this.acceptBtn,
      state.isFullyConsentedReviewMode
        ? this.options.reviewModeButtonText || "Continue"
        : this.getTranslatedText(
            "confirm_button_text",
            content?.confirm_button_text || DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT
          )
    );
    applySubmittingState(
      this.acceptAllBtn,
      this.getTranslatedText(
        "accept_all_button_text",
        content?.accept_all_button_text || DEFAULT_ACCEPT_ALL_BUTTON_TEXT
      )
    );

    if (this.declineBtn) {
      this.declineBtn.disabled = state.isSubmitting;
      this.declineBtn.style.opacity = state.isSubmitting ? "0.7" : "1";
      this.declineBtn.style.cursor = state.isSubmitting ? "not-allowed" : "pointer";
    }

    // Re-apply the required-elements gate, which `applySubmittingState` above
    // deliberately does not know about.
    this.updateAcceptButtonState(state);
  }
}
